// BookingFlowStep2 — the "Extras" step of the progressive booking
// flow. Rendered inline below the accommodation builder once the user
// has advanced past step 1. Extras are optional — the user can skip
// straight to step 3.
//
// Purely presentational; all state (selectedServiceKeys, flowStep) is
// read from the shared BookingContext through the ExtrasStep child.
//
// Extracted from `HomeBookingWidget.jsx`.

import { forwardRef } from "react";
import { ArrowRight, Check } from "lucide-react";
import { ExtrasStep } from "@/components/booking/BookingSteps";
import { COPY } from "@/components/booking/homeBookingWidgetCopy";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const BookingFlowStep2 = forwardRef(function BookingFlowStep2(
  { lang, flowStep, onBack, onContinue },
  ref
) {
  return (
    <div
      ref={ref}
      className="mt-10 pt-8 border-t border-[#D4A373]/40"
      data-testid="home-booking-step-2"
    >
      <div className="mb-5">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#A07042]">
          {pick(COPY.step2_overline, lang)}
        </p>
        <h3 className="mt-1 font-serif-x text-2xl md:text-3xl text-[#2C2621] leading-tight">
          {pick(COPY.step2_title, lang)}
        </h3>
        <p className="mt-2 text-sm text-[#5C5248] leading-relaxed max-w-2xl">
          {pick(COPY.step2_sub, lang)}
        </p>
      </div>

      <ExtrasStep lang={lang} />

      <div className="mt-6 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onBack}
          className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] hover:text-[#C16542] inline-flex items-center gap-1.5"
          data-testid="home-booking-back-1"
        >
          <ArrowRight className="w-3.5 h-3.5 rotate-180" strokeWidth={1.7} />
          {pick(COPY.cta_back, lang)}
        </button>
        {flowStep === 2 ? (
          <button
            type="button"
            onClick={onContinue}
            className="inline-flex items-center justify-center gap-3 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-8 py-4 text-xs tracking-[0.25em] uppercase transition-colors"
            data-testid="home-booking-continue-details"
          >
            {pick(COPY.cta_continue_details, lang)}
            <ArrowRight className="w-4 h-4" strokeWidth={1.6} />
          </button>
        ) : (
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#A07042] inline-flex items-center gap-2">
            <Check className="w-3.5 h-3.5" strokeWidth={2} />
            {pick(COPY.step_completed, lang)}
          </span>
        )}
      </div>
    </div>
  );
});

export default BookingFlowStep2;
